import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Alert, Spin } from 'antd';
import { api, getToken, setToken } from './api.js';

export type Account = {
  id: string;
  username: string;
  displayName: string;
  organizationId: string;
  isPlatformAdmin: boolean;
  capabilities: string[];
  serviceCategories: Array<'ONLINE' | 'ONSITE' | 'COMPONENT' | 'WHOLE_MACHINE'>;
  bonusManager?: boolean;
  bonusSelfView?: boolean;
};

type AuthState = {
  account: Account | null;
  loading: boolean;
  sessionEpoch: number;
  login(username: string, password: string): Promise<void>;
  logout(): Promise<void>;
  can(capability: string): boolean;
};

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [account, setAccount] = useState<Account | null>(null);
  const [loading, setLoading] = useState(Boolean(getToken()));
  const [sessionEpoch, setSessionEpoch] = useState(0);
  const generation = useRef(0);

  useEffect(() => {
    const refresh = () => {
      const current = ++generation.current;
      setSessionEpoch((value) => value + 1);
      setAccount(null);
      if (!getToken()) {
        setAccount(null);
        setLoading(false);
        return;
      }
      setLoading(true);
      void api<Account>('/auth/me').then((value) => { if (current === generation.current) setAccount(value); })
        .catch(() => { if (current === generation.current) setAccount(null); })
        .finally(() => { if (current === generation.current) setLoading(false); });
    };
    refresh();
    window.addEventListener('erp-auth-change', refresh);
    const onStorage = (event: StorageEvent) => { if (event.key === 'after-sales-erp-token' || event.key === null) refresh(); };
    window.addEventListener('storage', onStorage);
    return () => { ++generation.current; window.removeEventListener('erp-auth-change', refresh); window.removeEventListener('storage', onStorage); };
  }, []);

  const value = useMemo<AuthState>(() => ({
    account,
    loading,
    sessionEpoch,
    async login(username, password) {
      const result = await api<{ token: string; account: Account }>('/auth/login', {
        method: 'POST', body: JSON.stringify({ username, password })
      });
      setToken(result.token);
      ++generation.current;
      setAccount(result.account);
      setLoading(false);
    },
    async logout() {
      const logout = api('/auth/logout', { method: 'POST' });
      setToken(null); setAccount(null);
      await logout.catch(() => undefined);
    },
    can(capability) { return capability === 'BONUS_MANAGE' ? Boolean(account?.bonusManager)
      : capability === 'BONUS_SELF_VIEW' ? Boolean(account?.bonusManager || account?.bonusSelfView)
      : Boolean(account?.isPlatformAdmin || account?.capabilities.includes(capability)); }
  }), [account, loading, sessionEpoch]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const value = useContext(AuthContext);
  if (!value) throw new Error('AuthProvider is missing');
  return value;
}

export function RequireAuth({ children }: { children: React.ReactNode }) {
  const auth = useAuth();
  const location = useLocation();
  if (auth.loading) return <div className="screen-center"><Spin size="large" /></div>;
  if (!auth.account) return <Navigate to="/login" replace state={{ returnTo: location.pathname + location.search }} />;
  return children;
}

export function RequireAnyCapability({ capabilities, children }: { capabilities: string[]; children: React.ReactNode }) {
  const auth = useAuth();
  if (auth.loading) return <div className="screen-center"><Spin size="large" /></div>;
  if (!capabilities.some((capability) => auth.can(capability))) {
    return <div className="route-state"><Alert type="error" showIcon title="无权访问" description="当前账号没有访问此页面的权限。" /></div>;
  }
  return children;
}

export function RequirePlatformAdmin({children}:{children:React.ReactNode}) {
  const auth=useAuth();
  if (auth.loading) return <div className="screen-center"><Spin size="large" /></div>;
  if (!auth.account?.isPlatformAdmin) return <div className="route-state"><Alert type="error" showIcon title="无权访问" description="仅平台管理员可管理地图服务密钥。" /></div>;
  return children;
}
